// Phase 2D — Session cache for the Local Library page.
//
// Album rows live in memory only (Zustand + libraryAlbumIndex.ts). Connector
// SQLite is the source of truth; the web client fetches pages on demand.
//
// Persisted to localStorage: version, coverStats, fetchedAt — lightweight
// metadata for remote-viewer stale-while-revalidate only. Never persist the
// catalog blob (76k+ tracks exceeded WebView QuotaExceededError).

import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import {
  isLocalLibraryAlbumKey,
  normalizeLocalAlbumId,
} from '../services/localLibraryMetadata';

export interface CachedAlbum {
  id?: number | string;
  album_key?: string;
  artist_name?: string | null;
  title?: string | null;
  year?: number | null;
  genre?: string | null;
  format?: string | null;
  sample_rate?: number | null;
  bit_depth?: number | null;
  cover_art_url?: string | null;
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  user_override_source?: 'embedded' | 'folder' | 'online' | 'none' | null;
  enriched_at?: string | null;
  source_id?: string | null;
  machine_id?: string | null;
  track_count?: number;
  total_duration?: number;
  is_hires?: boolean;
  is_lossless?: boolean;
  is_compilation?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  __source?: string;
  isLocalLibrary?: boolean;
  // Carry through everything else the album-grid render path expects.
  [key: string]: any;
}

export interface CoverStats {
  total: number;
  embedded: number;
  folder: number;
  online: number;
  none: number;
  overridden: number;
  version?: string | null;
}

interface LibraryCacheState {
  /** Library version string (`<count>-<max_updated_at_iso>`). */
  version: string | null;
  /** In-session albums list — never written to localStorage. */
  albums: CachedAlbum[];
  /** Cover-source counts shown in the redesigned header. */
  coverStats: CoverStats | null;
  /** Wall-clock ms when the cache was last hydrated from the server. */
  fetchedAt: number;

  // ── mutators ────────────────────────────────────────────────────────
  setSnapshot: (
    albums: CachedAlbum[],
    version: string | null,
    coverStats?: CoverStats | null,
  ) => void;
  setCoverStats: (stats: CoverStats | null) => void;
  applyDiff: (added: CachedAlbum[], modified: CachedAlbum[], removedIds: Array<string | number>) => void;
  setVersion: (version: string | null) => void;
  /** Optimistic per-album mutation (e.g. cover-source override).
   *  Pass a partial patch keyed by id OR album_key. Bumps version client-side
   *  with a temporary value so a subsequent /version poll still picks up the
   *  authoritative server version.
   */
  patchAlbum: (
    matcher: { id?: number | string; album_key?: string },
    patch: Partial<CachedAlbum>,
    optimisticVersion?: string | null,
  ) => void;
  clear: () => void;
}

const STORAGE_KEY = 'maestro:libraryCache';

function isQuotaExceededError(err: unknown): boolean {
  if (err instanceof DOMException) {
    return err.name === 'QuotaExceededError' || err.code === 22;
  }
  const name = (err as { name?: string })?.name;
  if (name === 'QuotaExceededError') return true;
  const msg = String((err as Error)?.message ?? err ?? '');
  return /quota/i.test(msg);
}

/** localStorage adapter that degrades to no-cache instead of throwing. */
function createQuotaSafeStorage(): StateStorage {
  return {
    getItem: (name) => {
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      try {
        localStorage.setItem(name, value);
      } catch (err) {
        if (isQuotaExceededError(err)) {
          console.warn('[libraryCache] localStorage quota exceeded — skipping persist');
          try {
            localStorage.removeItem(name);
          } catch {
            /* ignore */
          }
          return;
        }
        console.warn('[libraryCache] localStorage setItem failed:', err);
      }
    },
    removeItem: (name) => {
      try {
        localStorage.removeItem(name);
      } catch {
        /* ignore */
      }
    },
  };
}

/** Strip legacy catalog blobs before Zustand hydrates (v4 and earlier). */
export function purgeLegacyPersistedCatalog(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { state?: Partial<LibraryCacheState> };
    const albums = parsed?.state?.albums;
    if (!Array.isArray(albums) || albums.length === 0) return;
    console.info(
      `[libraryCache] purging ${albums.length} legacy persisted album rows from localStorage`,
    );
    const next = {
      ...parsed,
      state: {
        version: parsed.state?.version ?? null,
        coverStats: parsed.state?.coverStats ?? null,
        fetchedAt: parsed.state?.fetchedAt ?? 0,
      },
      version: 5,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      if (isQuotaExceededError(err)) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

purgeLegacyPersistedCatalog();

function normalizeCachedAlbumRow(a: CachedAlbum): CachedAlbum {
  if (!a) return a;
  const raw = String(a.album_key || a.id || '');
  if (!raw) return a;
  const isLocal = a.isLocalLibrary || a.__source === 'local_library' || isLocalLibraryAlbumKey(raw);
  if (!isLocal) return a;
  const bare = normalizeLocalAlbumId(raw);
  if (bare === raw && String(a.id) === bare && String(a.album_key) === bare) return a;
  return { ...a, id: bare, album_key: bare };
}

function normalizeCachedAlbumList(albums: CachedAlbum[]): CachedAlbum[] {
  return (albums || []).map(normalizeCachedAlbumRow);
}

export const useLibraryCacheStore = create<LibraryCacheState>()(
  persist(
    (set, get) => ({
      version: null,
      albums: [],
      coverStats: null,
      fetchedAt: 0,

      setSnapshot: (albums, version, coverStats) => {
        set({
          albums: normalizeCachedAlbumList(Array.isArray(albums) ? albums : []),
          version: version || null,
          coverStats: coverStats !== undefined ? coverStats : get().coverStats,
          fetchedAt: Date.now(),
        });
      },

      setCoverStats: (stats) => {
        set({ coverStats: stats || null });
      },

      applyDiff: (added, modified, removedIds) => {
        const cur = get().albums;
        const removeSet = new Set((removedIds || []).map((x) => String(x)));
        // Build an index by id (preferred) AND album_key so the merge handles
        // both numeric ids and string keys depending on which the server returned.
        const byKey = new Map<string, CachedAlbum>();
        for (const a of cur) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (k && !removeSet.has(k)) byKey.set(k, a);
        }
        for (const a of modified || []) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (!k) continue;
          const prior = byKey.get(k);
          byKey.set(k, prior ? { ...prior, ...a } : a);
        }
        for (const a of added || []) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (!k || byKey.has(k)) continue;
          byKey.set(k, a);
        }
        // Re-sort by artist/title to match server ordering. The grid is
        // virtualized-by-page; stable order matters more than perfect locale.
        const next = Array.from(byKey.values()).sort((a, b) => {
          const ar = (a.artist_name || '').localeCompare(b.artist_name || '');
          if (ar !== 0) return ar;
          return (a.title || '').localeCompare(b.title || '');
        });
        set({ albums: normalizeCachedAlbumList(next), fetchedAt: Date.now() });
      },

      setVersion: (version) => set({ version: version || null }),

      patchAlbum: (matcher, patch, optimisticVersion) => {
        const cur = get().albums;
        const next = cur.map((a) => {
          const idMatch = matcher.id != null && (a?.id != null) && String(a.id) === String(matcher.id);
          const keyMatch = matcher.album_key && a?.album_key === matcher.album_key;
          return idMatch || keyMatch ? { ...a, ...patch } : a;
        });
        set({
          albums: next,
          version: optimisticVersion !== undefined ? optimisticVersion : get().version,
        });
      },

      clear: () => set({ version: null, albums: [], coverStats: null, fetchedAt: 0 }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => createQuotaSafeStorage()),
      // Never persist albums — Connector SQLite / live API are authoritative.
      partialize: (state) => ({
        version: state.version,
        coverStats: state.coverStats,
        fetchedAt: state.fetchedAt,
      }),
      version: 5,
      migrate: (persisted: unknown, fromVersion: number) => {
        const stripAlbums = (state: Partial<LibraryCacheState>) => ({
          version: state.version ?? null,
          coverStats: state.coverStats ?? null,
          fetchedAt: state.fetchedAt ?? 0,
        });

        // v2–v4: remote tunnel + local rows — drop any persisted catalog blob.
        if (fromVersion < 5) {
          return stripAlbums(persisted as Partial<LibraryCacheState>);
        }
        const state = persisted as Partial<LibraryCacheState>;
        if (fromVersion < 4 && Array.isArray(state.albums)) {
          return stripAlbums(state);
        }
        return stripAlbums(state);
      },
    },
  ),
);
